import * as React from "react";
import { Link } from "react-router";
import InfoHint from "../info-hint";
import { reachGoal } from "../../../analytics/metrika";

export type NavigationItem = {
  label: string;
  path: string;
  hint?: string;
  isNewTab?: boolean;
  isExternal?: boolean;
  download?: boolean | string;
};

type LinkLikeProps = {
  to: string;
  className?: string;
  children: React.ReactNode;
  newTab?: boolean;
  external?: boolean;
  download?: boolean | string;
  onClick?: React.MouseEventHandler<HTMLElement>;
};

interface INavigationMenuProps {
  /** Для подсветки активного пункта (в Storybook задаёшь руками) */
  activePath: string;

  /** Можно переопределить пункты */
  navItems?: NavigationItem[];

  /**
   * Компонента ссылки. В приложении по умолчанию будет react-router Link,
   * а в Storybook можно подложить простой <a>.
   */
  LinkComponent?: React.ComponentType<LinkLikeProps>;
}

const defaultNavItems: NavigationItem[] = [
  { label: "Техн Радар", path: "/" },
  { label: "StoryBook", path: "/storybook" },
  { label: "Скачать", path: "/download" },
  { label: "Контакт", path: "/contact" },
  { label: "Моё CV", path: "/my-cv" },
];

const RouterLink: React.FC<LinkLikeProps> = ({
  to,
  className,
  children,
  newTab,
  external,
  download,
  onClick,
}) => {
  if (external && !newTab) {
    return (
      <a
        href={to}
        className={className}
        target="_self"
        download={download}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }

  return (
    <Link
      to={to}
      className={className}
      target={newTab ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      onClick={onClick}
    >
      {children}
    </Link>
  );
};

const NavigationMenu: React.FunctionComponent<INavigationMenuProps> = ({
  activePath,
  navItems = defaultNavItems,
  LinkComponent = RouterLink,
}) => {
  const isActive = (path: string) => {
    if (path === "/") return activePath === "/";
    return activePath === path;
  };

  return (
    <div
      className="relative content-stretch flex items-center"
      data-name="navigation"
    >
      <div
        className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0"
        data-name="nav-menu-buttons"
      >
        {navItems.map((item) => (
          <div
            key={item.path}
            className="content-stretch flex items-center gap-[6px] relative shrink-0"
          >
            <LinkComponent
              to={item.path}
              className="content-stretch flex items-center relative shrink-0 cursor-pointer hover:opacity-70 transition-opacity"
              newTab={item.isNewTab}
              external={item.isExternal}
              download={item.download}
              onClick={() => {
                if (item.download) reachGoal("cv_download");
                else if (item.path.includes("/techradar/"))
                  reachGoal("radar_open");
              }}
            >
              <p
                className={`font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[normal] not-italic relative shrink-0 text-[20px] ${
                  isActive(item.path) ? "text-blue-600" : "text-black"
                }`}
              >
                {item.label}
              </p>
            </LinkComponent>
            {item.hint && <InfoHint text={item.hint} placement="right" />}
          </div>
        ))}
      </div>
    </div>
  );
};

export default NavigationMenu;
export type { LinkLikeProps };
